import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";

import { getInitialFilterValuesUseFilterHistory } from "utilities/list/list.types";

import { FilterHandler, type IFilterHandler } from "entity/filters/Filter/Filter.api.handler";
import {
    type FilterEntity,
    type FilterEntityType,
    getSelectedFilterItemsFromSavedFilter,
    mapFilterEntityToFormValues,
    type SystemFilterType,
} from "entity/filters/Filter/Filter.api.types";
import { type IFilterHistoryState } from "entity/filters/Filter/Filter.reducer";
import { type IFilterDrawerHistoryState } from "entity/filters/Filter/FilterDrawer";
import {
    applySerializeBehavior,
    getAppliedFilterCount,
    prepareFiltersForSubmission,
} from "entity/filters/filters.utils";

interface IFilterLoadingHook {
    isLoading: true;
}

interface IFilterLoadedHook {
    isLoading: false;
    filterState: IFilterHistoryState;
    filterEntity: FilterEntity;
    reload: (id?: number) => Promise<void>;
}

const defaultFilterHandler = new FilterHandler();

export const useFilterHistoryState = (
    state: IFilterDrawerHistoryState,
    filterTypeEntity: FilterEntityType,
    jobIds: number[] | undefined,
    systemFilter: SystemFilterType | undefined,
    // Optional handler override. Defaults to the legacy `/api/Filters/{id}` handler; pass a custom handler
    // (e.g. the BTAdmin online-payment handler) to load the entity from a different, no-GlobalUser source.
    filterHandler: IFilterHandler = defaultFilterHandler,
): IFilterLoadedHook | IFilterLoadingHook => {
    const navigate = useNavigate();
    const location = useLocation();
    const trimmedState: IFilterHistoryState | undefined =
        state?.filterState && state.filterState.filterType === filterTypeEntity
            ? {
                  appliedFilter: state.filterState.appliedFilter,
                  selectedFilterId: state.filterState.selectedFilterId,
                  appliedFiltersFromSavedFilter: state.filterState.appliedFiltersFromSavedFilter,
              }
            : undefined;

    const onChange = useCallback(
        (newState: IFilterHistoryState) => {
            if (newState) {
                const newHistoryState: IFilterDrawerHistoryState = {
                    filterState: { ...newState, filterType: filterTypeEntity },
                };
                navigate(location.pathname + location.search, {
                    replace: true,
                    state: {
                        ...(typeof state === "object" ? state : {}),
                        ...newHistoryState,
                    },
                });
            }
        },
        [navigate, filterTypeEntity, location, state],
    );

    const filters = useFilterState(
        trimmedState,
        filterTypeEntity,
        onChange,
        jobIds,
        systemFilter,
        filterHandler,
    );

    return filters;
};

export const useFilterState = (
    state: IFilterHistoryState | undefined,
    filterTypeEntity: FilterEntityType,
    onChange: (state: IFilterHistoryState) => void,
    jobIds: number[] | undefined,
    systemFilter: SystemFilterType | undefined,
    filterHandler: IFilterHandler = defaultFilterHandler,
): IFilterLoadedHook | IFilterLoadingHook => {
    const [filterEntity, setFilterEntity] = useState<FilterEntity | undefined>(undefined);

    // This function accepts an id because if you save a filter, we apply that filter immediately.
    const reloadFilterEntity = useCallback(
        async (id?: number) => {
            const filterEntity = await getInitialFilterValuesUseFilterHistory(
                filterTypeEntity,
                filterHandler,
                jobIds,
                systemFilter,
            );
            setFilterEntity(filterEntity);

            if (id !== undefined) {
                const values = mapFilterEntityToFormValues(filterEntity, id);

                prepareFiltersForSubmission(values, filterEntity);

                const correctedFilterValues = applySerializeBehavior(filterEntity, values);

                const newState: IFilterHistoryState = {
                    selectedFilterId: id,
                    appliedFilter: correctedFilterValues.items,
                    appliedFiltersFromSavedFilter: true,
                };

                onChange(newState);
            }
        },
        [filterHandler, setFilterEntity, onChange, filterTypeEntity, jobIds, systemFilter],
    );

    // This useEffect loads the filter entity
    useEffect(() => {
        void reloadFilterEntity();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const result = useMemo(() => {
        if (!state && filterEntity) {
            const filterValues = applySerializeBehavior(
                filterEntity,
                mapFilterEntityToFormValues(filterEntity),
            );
            const initialFilterObj: IFilterHistoryState = {
                appliedFilter: filterValues.items,
                appliedFiltersFromSavedFilter: true,
                selectedFilterId: filterValues.savedFilter.id,
            };

            return initialFilterObj;
        }

        return state;
    }, [filterEntity, state]);

    if (!filterEntity || !result) {
        return { isLoading: true };
    }

    return {
        filterState: result,
        filterEntity: filterEntity,
        isLoading: false,
        reload: reloadFilterEntity,
    };
};

/**
 * Entity-injected variant of {@link useFilterState}: the caller already loaded the `FilterEntity` itself
 * (e.g. via an orval `useQuery` hook), so there is no `IFilterHandler` and no fetching here. It only
 * bootstraps the initial applied-filter form state from the entity — identical to `useFilterState` — and
 * returns `reload` (the caller's refetch). Used by BTAdmin grids whose filter options come from a
 * no-GlobalUser APIX endpoint instead of the legacy `/api/Filters/{id}` handler.
 */
export const useFilterStateFromEntity = (
    filterEntity: FilterEntity | undefined,
    state: IFilterHistoryState | undefined,
    reload: (id?: number) => Promise<void>,
): IFilterLoadedHook | IFilterLoadingHook => {
    const result = useMemo(() => {
        if (!state && filterEntity) {
            const filterValues = applySerializeBehavior(
                filterEntity,
                mapFilterEntityToFormValues(filterEntity),
            );

            return {
                appliedFilter: filterValues.items,
                appliedFiltersFromSavedFilter: true,
                selectedFilterId: filterValues.savedFilter.id,
            };
        }

        return state;
    }, [filterEntity, state]);

    if (!filterEntity || !result) {
        return { isLoading: true };
    }

    return {
        filterState: result,
        filterEntity,
        isLoading: false,
        reload,
    };
};

export const useFilterHistoryCount = (filterEntity: FilterEntity, state: IFilterHistoryState) => {
    const appliedFilterCount = useMemo(
        () =>
            state && filterEntity
                ? getAppliedFilterCount(
                      state.appliedFilter ||
                          getSelectedFilterItemsFromSavedFilter(
                              filterEntity,
                              filterEntity.selectedSystemFilter,
                          ),
                      filterEntity,
                  )
                : 0,
        [filterEntity, state],
    );

    return appliedFilterCount;
};
